/**
 * Serviço para buscar metadados de referências bibliográficas
 * usando DOI, ISBN ou ISSN
 */

import { CreateWorkInput, WorkType } from '../types/reference';

export interface MetadataResult {
  success: boolean;
  data?: Partial<CreateWorkInput>;
  error?: string;
}

/**
 * Busca metadados usando DOI via CrossRef API
 */
export async function fetchByDOI(doi: string): Promise<MetadataResult> {
  try {
    // Limpar DOI (remover espaços, prefixos, etc)
    const cleanDOI = doi.trim().replace(/^(doi:|https?:\/\/(dx\.)?doi\.org\/)/, '');
    
    const response = await fetch(`https://api.crossref.org/works/${encodeURIComponent(cleanDOI)}`);
    
    if (!response.ok) {
      return {
        success: false,
        error: 'DOI não encontrado ou inválido'
      };
    }

    const data = await response.json();
    const work = data.message;

    // Mapear tipo de publicação
    let type: WorkType = 'other';
    if (work.type === 'journal-article') type = 'article';
    else if (work.type === 'book' || work.type === 'monograph') type = 'book';
    else if (work.type === 'dissertation') type = 'thesis';
    else if (work.type === 'report') type = 'report';

    // Extrair autores
    const authors = work.author?.map((a: { given?: string; family?: string }) =>
      `${a.given || ''} ${a.family || ''}`.trim()
    ) || [];

    // Extrair ano
    const year = work.published?.['date-parts']?.[0]?.[0] || 
                 work.created?.['date-parts']?.[0]?.[0] || 
                 new Date().getFullYear();

    return {
      success: true,
      data: {
        type,
        title: work.title?.[0] || '',
        authors,
        year,
        publisher: work.publisher || undefined,
        doi: cleanDOI,
        url: work.URL || `https://doi.org/${cleanDOI}`,
        abstract: work.abstract || undefined,
        format: 'digital'
      }
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Erro ao buscar metadados por DOI'
    };
  }
}

/**
 * Busca metadados usando ISBN via Open Library API
 */
export async function fetchByISBN(isbn: string): Promise<MetadataResult> {
  try {
    // Limpar ISBN (remover hífens, espaços)
    const cleanISBN = isbn.replace(/[-\s]/g, '');
    
    const response = await fetch(`https://openlibrary.org/api/books?bibkeys=ISBN:${cleanISBN}&format=json&jscmd=data`);
    
    if (!response.ok) {
      return {
        success: false,
        error: 'ISBN não encontrado ou inválido'
      };
    }

    const data = await response.json();
    const bookKey = `ISBN:${cleanISBN}`;
    const book = data[bookKey];

    if (!book) {
      return {
        success: false,
        error: 'Nenhum resultado encontrado para este ISBN'
      };
    }

    // Extrair autores
    const authors = book.authors?.map((a: { name: string }) => a.name) || [];

    // Extrair ano
    const year = book.publish_date ? parseInt(book.publish_date) : new Date().getFullYear();

    return {
      success: true,
      data: {
        type: 'book',
        title: book.title || '',
        authors,
        year: isNaN(year) ? new Date().getFullYear() : year,
        publisher: book.publishers?.[0]?.name || undefined,
        isbn: cleanISBN,
        url: book.url || `https://openlibrary.org/isbn/${cleanISBN}`,
        format: 'physical'
      }
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Erro ao buscar metadados por ISBN'
    };
  }
}

/**
 * Busca metadados usando ISSN via Portal de Periódicos ISSN
 * Nota: A API pública do ISSN é limitada, esta implementação é simplificada
 */
export async function fetchByISSN(issn: string): Promise<MetadataResult> {
  try {
    // Limpar ISSN (remover hífens, espaços)
    const cleanISSN = issn.replace(/[-\s]/g, '');
    
    // Formatar ISSN (XXXX-XXXX)
    const formattedISSN = `${cleanISSN.slice(0, 4)}-${cleanISSN.slice(4)}`;
    
    // Tentar buscar via CrossRef (muitos periódicos estão cadastrados)
    const response = await fetch(
      `https://api.crossref.org/journals/${encodeURIComponent(formattedISSN)}`
    );
    
    if (!response.ok) {
      return {
        success: false,
        error: 'ISSN não encontrado ou inválido'
      };
    }

    const data = await response.json();
    const journal = data.message;

    return {
      success: true,
      data: {
        type: 'article',
        title: journal.title || '',
        authors: [],
        year: new Date().getFullYear(),
        publisher: journal.publisher || undefined,
        url: journal.URL || undefined,
        format: 'digital'
      }
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Erro ao buscar metadados por ISSN'
    };
  }
}

/**
 * Detecta automaticamente o tipo de identificador e busca metadados
 */
export async function fetchMetadata(identifier: string): Promise<MetadataResult> {
  const cleaned = identifier.trim();
  
  // Detectar DOI (10.xxxx/xxxx ou doi:10.xxxx/xxxx ou https://doi.org/10.xxxx/xxxx)
  if (/^(doi:)?(https?:\/\/(dx\.)?doi\.org\/)?10\.\d{4,}/.test(cleaned)) {
    return fetchByDOI(cleaned);
  }
  
  // Detectar ISBN (10 ou 13 dígitos, com ou sem hífens)
  if (/^[\d-]{10,17}$/.test(cleaned.replace(/\s/g, ''))) {
    const digits = cleaned.replace(/[-\s]/g, '');
    if (digits.length === 10 || digits.length === 13) {
      return fetchByISBN(cleaned);
    }
  }
  
  // Detectar ISSN (XXXX-XXXX ou 8 dígitos)
  if (/^\d{4}-?\d{3}[\dxX]$/.test(cleaned.replace(/\s/g, ''))) {
    return fetchByISSN(cleaned);
  }
  
  return {
    success: false,
    error: 'Identificador não reconhecido. Use DOI, ISBN ou ISSN.'
  };
}
